import React, { createRef, FC, ForwardedRef, forwardRef, useMemo, useState } from "react";
import { Button, Text, Box, Flex, Input, ButtonProps } from "@chakra-ui/react";
import { useDebounce, useMount } from "react-use";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { colours } from "@/utils/themeUtils";
import {
	ONE_DIGIT,
	parsePreselectedSessionTime,
	validate,
	validate24Hour,
	ZERO_TO_TWO_DIGITS,
} from "./ClassroomSessionTimeSelectorHelpers";

dayjs.extend(customParseFormat);

interface TimeSelectorProps {
	preselected?: string | string[] | number | number[] | null;
	onChange: (value: number | null, error: string | null) => void;
	/** When true, show 24-hour hours (00–23) and hide AM/PM; onChange still receives HHmm. */
	use24Hour?: boolean;
	options?: {
		validateFuture?: boolean;
		minTime?: string | number | null;
		maxTime?: string | number | null;
		autoFocus?: boolean;
	};
}

export const ClassroomSessionTimeSelector: FC<TimeSelectorProps> = (props) => {
	const use24Hour = props.use24Hour ?? false;

	const init = () => {
		if (props.preselected) {
			const raw = Array.isArray(props.preselected) ? props.preselected[0] : props.preselected;
			const timeObject = parsePreselectedSessionTime(raw);
			if (timeObject) {
				if (use24Hour) {
					return {
						initHour: timeObject.format("HH"),
						initMinute: timeObject.format("mm"),
						initPeriod: "AM",
						initError: null,
					};
				}
				return {
					initHour: timeObject.format("hh"),
					initMinute: timeObject.format("mm"),
					initPeriod: timeObject.format("A"),
					initError: null,
				};
			}
		}
		return {
			initHour: "",
			initMinute: "",
			initPeriod: "AM",
			initError: null,
		};
	};

	const inputHourRef = createRef<HTMLInputElement>();
	const inputMinuteRef = createRef<HTMLInputElement>();

	const { initHour, initMinute, initPeriod, initError } = init();
	const [inputHour, setInputHour] = useState(initHour);
	const [inputMinute, setInputMinute] = useState(initMinute);
	const [inputPeriod, setInputPeriod] = useState(initPeriod);
	const [error, setError] = useState<string | null>(initError);

	useMount(() => {
		const shouldAutoFocus = props.options?.autoFocus ?? true;
		if (shouldAutoFocus) {
			inputHourRef.current?.focus();
		}
	});

	const shouldValidateFuture = useMemo(() => props.options?.validateFuture ?? true, [props.options?.validateFuture]);

	const minTimeObject = useMemo(() => {
		if (props.options?.minTime === undefined || props.options?.minTime === null || props.options?.minTime === "") {
			return undefined;
		}
		const t = parsePreselectedSessionTime(props.options.minTime);
		if (!t) {
			return undefined;
		}
		return t.add(1, "minute");
	}, [props.options?.minTime]);

	const maxTimeObject = useMemo(() => {
		if (props.options?.maxTime === undefined || props.options?.maxTime === null || props.options?.maxTime === "") {
			return undefined;
		}
		const t = parsePreselectedSessionTime(props.options.maxTime);
		if (!t) {
			return undefined;
		}
		return t.subtract(1, "minute");
	}, [props.options?.maxTime]);

	const process = () => {
		const { result, error: nextError } = use24Hour
			? validate24Hour(inputHour, inputMinute, shouldValidateFuture, minTimeObject, maxTimeObject)
			: validate(inputHour, inputMinute, inputPeriod, shouldValidateFuture, minTimeObject, maxTimeObject);
		setError(nextError);
		props.onChange(result, nextError);
	};

	useMount(process);

	useDebounce(process, 100, [inputHour, inputMinute, inputPeriod, minTimeObject, maxTimeObject, use24Hour, shouldValidateFuture]);

	const hourPlaceholder = use24Hour ? "HH" : "hh";

	return (
		<Box>
			<Flex alignItems="center" gap={1}>
				<Input
					data-testid="hour-input"
					ref={inputHourRef}
					value={inputHour}
					maxLength={2}
					placeholder={hourPlaceholder}
					bg="white"
					width="40px"
					px={2.5}
					py={0}
					onBlur={(e) => {
						if (ONE_DIGIT.test(e.target.value)) {
							setInputHour(`0${e.target.value}`);
						}
					}}
					onChange={(e) => {
						if (!ZERO_TO_TWO_DIGITS.test(e.target.value)) {
							return;
						}
						if (e.target.value.length <= 2) {
							setInputHour(e.target.value);
						}
						if (e.target.value.length === 2) {
							inputMinuteRef.current?.focus();
						}
					}}
					onFocus={(e) => {
						e.target.select();
					}}
				/>
				<Text>:</Text>
				<Input
					data-testid="minute-input"
					ref={inputMinuteRef}
					value={inputMinute}
					maxLength={2}
					placeholder="mm"
					bg="white"
					width="40px"
					px={2.5}
					py={0}
					onBlur={(e) => {
						if (ONE_DIGIT.test(e.target.value)) {
							setInputMinute(`0${e.target.value}`);
						}
					}}
					onChange={(e) => {
						if (!ZERO_TO_TWO_DIGITS.test(e.target.value)) {
							return;
						}
						if (e.target.value.length <= 2) {
							setInputMinute(e.target.value);
						}
					}}
					onFocus={(e) => {
						e.target.select();
					}}
				/>
				{!use24Hour && (
					<Flex alignItems="center" ml={1}>
						<PeriodButton
							active={inputPeriod === "AM"}
							onClick={() => {
								setInputPeriod("AM");
							}}
						>
							AM
						</PeriodButton>
						<PeriodButton
							active={inputPeriod === "PM"}
							onClick={() => {
								setInputPeriod("PM");
							}}
						>
							PM
						</PeriodButton>
					</Flex>
				)}
			</Flex>
			{error && (
				<Text className="error" color={colours.danger.background.strong} width="209px" p="4px 0 0 4px">
					{error}
				</Text>
			)}
		</Box>
	);
};

interface PeriodButtonProps extends ButtonProps {
	active: boolean;
}

const PeriodButton = forwardRef(({ active, onClick, children }: PeriodButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
	let testId = undefined;
	if (typeof children === "string") {
		if (children.toLowerCase() === "am") {
			testId = "am-button";
		} else if (children.toLowerCase() === "pm") {
			testId = "pm-button";
		}
	}
	return (
		<Button
			ref={ref}
			data-testid={testId}
			data-test-active={active}
			onClick={onClick}
			width="40px"
			fontSize={"sm"}
			py={0}
			color={active ? "neutrals.0" : "neutrals.900"}
			bgColor={active ? "neutrals.500" : "neutrals.0"}
			borderColor={active ? "neutrals.500" : "neutrals.50"}
			_hover={{
				bgColor: active ? "neutrals.500" : "neutrals.40",
			}}
			_first={{
				borderRadius: "8px 0 0 8px",
				borderWidth: "1px 0 1px 1px",
			}}
			_last={{
				borderRadius: "0 8px 8px 0",
				borderWidth: "1px 1px 1px 0",
			}}
		>
			{children}
		</Button>
	);
});
